import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import HistoricoGeral from '../app/components/HistoricoGeral';
import axios from '../../../utils/axios';
import { Num, semAcento, BotaoExportar } from '../../../components/gps';
import TituloTela from '../../../components/gps/TituloTela';
import CaixaBusca from '../../../components/gps/CaixaBusca';
import { isMaster } from '../../../utils/perfil';
import { cn } from '../../../lib/utils';

/* ============================================================================
   Logs do sistema — Configurações, somente Master.

   O QUE ESTA TELA É: um espelho das últimas ~2000 linhas que a API registrou,
   guardadas em memória do processo (ver `logs-memoria.service.ts` no backend).
   Serve para acompanhar o sistema enquanto se testa e para entender um erro que
   acabou de acontecer, sem abrir terminal no servidor.

   O QUE ELA NÃO É, e está escrito na própria tela: histórico. Os registros
   somem quando a API reinicia e valem só para o processo que respondeu. Para
   investigar ontem, continua sendo `pm2 logs`. Dizer isso no rodapé evita a
   conclusão errada de que "não há erro" quando na verdade houve um deploy.

   PERMISSÃO EM TRÊS CAMADAS, e a de baixo é a que vale:
     1. o item não entra no menu de quem não é Master (NavConfig);
     2. esta página recusa antes de pedir qualquer coisa;
     3. a ROTA recusa no servidor - é a única que impede de verdade.
   As duas primeiras existem para não oferecer o que não se pode usar; sem a
   terceira, bastaria digitar a URL.
   ============================================================================ */

type EntradaLog = {
  id: number;
  data: string;
  nivel: 'log' | 'error' | 'warn' | 'debug' | 'verbose';
  contexto: string;
  mensagem: string;
  pilha?: string;
};

type Resposta = {
  itens: EntradaLog[];
  total: number;
  contagem: { error: number; warn: number; guardados: number; capacidade: number };
  ultimoId: number;
};

type Webhook = {
  codigo: number;
  deviceId: string | null;
  name: string | null;
  imei: string | null;
  serialNumber: string | null;
  status: string;
  statusWebhook: string | null;
  errorMessage: string | null;
  tentativas: number;
  origem: string;
  createdAt: string;
  payloadBytes: number;
};

const NIVEIS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'error', rotulo: 'Erros' },
  { valor: 'warn', rotulo: 'Avisos' },
  { valor: 'log', rotulo: 'Informação' },
  { valor: 'debug', rotulo: 'Depuração' },
];

/* Cor por nível. Erro em vermelho e aviso em âmbar é convenção que não se
   inventa; `log` fica neutro de propósito - se tudo tiver cor, nada tem. */
const TOM: Record<string, string> = {
  error: 'bg-danger-soft text-danger',
  warn: 'bg-warn-soft text-warn',
  log: 'bg-soft text-muted-foreground',
  debug: 'bg-info-soft text-info',
  verbose: 'bg-soft text-faint',
};

function hora(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function dia(iso: string) {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('pt-BR') : '';
}

LogsSistema.getLayout = function getLayout(pagina: React.ReactElement) {
  return <Layout>{pagina}</Layout>;
};

export default function LogsSistema() {
  const { user } = useAuth();
  const master = isMaster(user);

  const [itens, setItens] = useState<EntradaLog[]>([]);
  const [contagem, setContagem] = useState<Resposta['contagem'] | null>(null);
  const [nivel, setNivel] = useState('todos');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [auto, setAuto] = useState(true);
  const [expandido, setExpandido] = useState<number | null>(null);

  /* DUAS ABAS NA MESMA TELA, e nao dois itens de menu: sao a mesma pergunta
     ("o que o sistema registrou?") com duas fontes, e a permissao e a mesma.
     Dois itens de menu fariam o Master escolher antes de saber o que procura. */
  const [aba, setAba] = useState<'aplicacao' | 'webhooks' | 'arquivos' | 'historico'>('aplicacao');

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [totalWebhooks, setTotalWebhooks] = useState(0);
  const [paginaWebhook, setPaginaWebhook] = useState(0);
  const [statusWebhook, setStatusWebhook] = useState('todos');
  // `buscaWebhook` vai a API so no Enter ou na lupa (21/10/2026) - antes ia 350 ms
  // depois de cada pausa na digitacao, um GET /logs/webhooks por pausa.
  const [buscaWebhook, setBuscaWebhook] = useState('');
  const pedidoWebhook = useRef(0);
  const [payload, setPayload] = useState<{ codigo: number; texto: string } | null>(null);

  /* "As vezes chega dispositivo so com IMEI, mas normalmente o Google manda o
     serial" - esse filtro isola exatamente esse caso, que e o motivo de a tela
     existir. Sem ele, achar os poucos sem serial seria varrer pagina por
     pagina. Filtra no cliente porque age sobre a pagina que ja esta na mao. */
  const [soSemSerial, setSoSemSerial] = useState(false);

  const [arquivos, setArquivos] = useState<Array<{ nome: string; pasta: string; bytes: number; modificado: string }>>([]);
  const [pastasLidas, setPastasLidas] = useState<string[]>([]);
  const [arquivoAberto, setArquivoAberto] = useState<{ nome: string; pasta: string; conteudo: string } | null>(null);

  /* O filtro de nível vai ao SERVIDOR e a busca fica no cliente.

     Não é inconsistência: o nível reduz o volume trafegado (e o servidor já
     tem os registros separados), enquanto a busca muda a cada tecla - mandar
     uma requisição por caractere numa tela que já atualiza sozinha seria
     ruído de rede para filtrar uma lista que já está na mão. */
  const carregar = useCallback(async () => {
    if (!master) return;
    setCarregando(true);
    try {
      const r = await axios.get<Resposta>(`/logs/sistema?nivel=${encodeURIComponent(nivel)}&limite=500`);
      setItens(r.data?.itens ?? []);
      setContagem(r.data?.contagem ?? null);
      setErro('');
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível ler os logs.');
    } finally {
      setCarregando(false);
    }
  }, [master, nivel]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /* ATUALIZAÇÃO AUTOMÁTICA a cada 5s, e o `ref` é o que impede o efeito de se
     reinscrever a cada render: com `carregar` na lista de dependências, todo
     render novo derrubaria e recriaria o intervalo, e em render frequente ele
     nunca chegaria a disparar. */
  const refCarregar = useRef(carregar);
  refCarregar.current = carregar;

  useEffect(() => {
    /* So na aba de aplicacao: na de webhooks a fonte e tabela, o volume e
       maior e recarregar sozinho tiraria a pagina debaixo de quem esta lendo. */
    if (!auto || !master || aba !== 'aplicacao') return undefined;
    const id = setInterval(() => refCarregar.current(), 5000);
    return () => clearInterval(id);
  }, [auto, master, aba]);

  /* A BUSCA DOS WEBHOOKS VAI AO SERVIDOR - ao contrario da aba de aplicacao,
     onde ela filtra no cliente. A diferenca e a fonte: ali sao 500 linhas ja
     carregadas; aqui a tabela tem o historico inteiro, e filtrar no cliente
     acharia so dentro da pagina atual - o que da a impressao errada de que o
     registro procurado nao existe. */
  const carregarWebhooks = useCallback(async () => {
    if (!master || aba !== 'webhooks') return;
    const meuPedido = ++pedidoWebhook.current;
    setCarregando(true);
    try {
      const r = await axios.get<{ content: Webhook[]; totalElements: number }>(
        `/logs/webhooks?page=${paginaWebhook}&rows=25` +
          `&status=${encodeURIComponent(statusWebhook)}&busca=${encodeURIComponent(buscaWebhook.trim())}`
      );
      if (meuPedido !== pedidoWebhook.current) return; // resposta de busca antiga
      setWebhooks(r.data?.content ?? []);
      setTotalWebhooks(r.data?.totalElements ?? 0);
      setErro('');
    } catch (e: any) {
      if (meuPedido !== pedidoWebhook.current) return;
      setErro(e?.response?.data?.message || 'Não foi possível ler os webhooks.');
      setWebhooks([]);
      setTotalWebhooks(0);
    } finally {
      if (meuPedido === pedidoWebhook.current) setCarregando(false);
    }
  }, [master, aba, paginaWebhook, statusWebhook, buscaWebhook]);

  useEffect(() => {
    carregarWebhooks();
  }, [carregarWebhooks]);

  async function abrirPayload(codigo: number) {
    if (payload?.codigo === codigo) {
      setPayload(null);
      return;
    }
    try {
      const r = await axios.get<{ payload: string; resposta: string | null }>(`/logs/webhooks/${codigo}`);
      /* JSON reformatado para leitura. Se vier texto que nao e JSON valido,
         mostra como chegou - engolir o conteudo por causa do formato seria
         esconder justamente o caso estranho. */
      let texto = r.data?.payload || '';
      try {
        texto = JSON.stringify(JSON.parse(texto), null, 2);
      } catch {
        /* fica como veio */
      }
      if (r.data?.resposta) texto += '\n\n--- resposta ---\n' + r.data.resposta;
      setPayload({ codigo, texto });
    } catch {
      setPayload({ codigo, texto: 'Não foi possível carregar o payload.' });
    }
  }

  const carregarArquivos = useCallback(async () => {
    if (!master || aba !== 'arquivos') return;
    setCarregando(true);
    try {
      const r = await axios.get<{ arquivos: typeof arquivos; pastasLidas: string[] }>('/logs/arquivos');
      setArquivos(r.data?.arquivos ?? []);
      setPastasLidas(r.data?.pastasLidas ?? []);
      setErro('');
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Nao foi possivel listar os arquivos.');
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master, aba]);

  useEffect(() => {
    carregarArquivos();
  }, [carregarArquivos]);

  async function abrirArquivo(nome: string) {
    if (arquivoAberto?.nome === nome) {
      setArquivoAberto(null);
      return;
    }
    try {
      const r = await axios.get<{ nome: string; pasta: string; conteudo: string }>(
        `/logs/arquivos/conteudo?nome=${encodeURIComponent(nome)}&linhas=1000`
      );
      setArquivoAberto(r.data);
    } catch (e: any) {
      setArquivoAberto({ nome, pasta: '', conteudo: e?.response?.data?.message || 'Nao foi possivel ler o arquivo.' });
    }
  }

  /* BAIXAR UM PAYLOAD como .json.

     Um `Blob` local e `URL.createObjectURL`, e nao um link para a API: o
     conteudo ja esta na tela, entao pedir de novo ao servidor seria uma ida a
     mais para o mesmo texto - e daria para baixar sem o cabecalho de
     autenticacao, que o axios poe e um `<a href>` nao poria.

     `revokeObjectURL` depois: cada Blob nao liberado fica preso na memoria da
     aba ate a pagina ser recarregada. */
  function baixarPayload(codigo: number, texto: string) {
    const blob = new Blob([texto], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `webhook-${codigo}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const webhooksVisiveis = useMemo(
    () => (soSemSerial ? webhooks.filter((w) => !w.serialNumber) : webhooks),
    [webhooks, soSemSerial]
  );

  const filtrados = useMemo(() => {
    const alvo = semAcento(busca.trim());
    if (!alvo) return itens;
    const termos = alvo.split(/\s+/);
    return itens.filter((e) => {
      const texto = semAcento(`${e.contexto} ${e.mensagem} ${e.pilha || ''}`);
      return termos.every((t) => texto.includes(t));
    });
  }, [itens, busca]);

  if (!master) {
    return (
      <Page title="Logs do sistema">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 py-10">
          <div className="rounded-lg border border-warn bg-warn-soft p-4 text-[12.5px]">
            Esta tela é restrita ao perfil Master.
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page title="Logs do sistema">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <TituloTela descricao="Últimas linhas registradas pela API. Guardadas em memória: reiniciar o servidor limpa a lista.">
            Logs do sistema
          </TituloTela>

          <div className="flex flex-wrap items-center gap-2">
            {/* A contagem de erros fica SEMPRE visível, mesmo olhando outro
                nível: é a informação que faz alguém abrir esta tela. */}
            {contagem && (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-soft px-2 py-0.5 text-[11px] font-bold text-danger">
                  <Num>{contagem.error}</Num> erro(s)
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-soft px-2 py-0.5 text-[11px] font-bold text-warn">
                  <Num>{contagem.warn}</Num> aviso(s)
                </span>
              </>
            )}

            {/* So na aba Aplicacao: nas outras ele recarregava os logs da aplicacao,
                nao o que estava na tela (cada aba tem o proprio "Atualizar"). */}
            {aba === 'aplicacao' && (
              <button
                type="button"
                onClick={carregar}
                disabled={carregando}
                className="h-8 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
              >
                {carregando ? 'Lendo...' : '↻ Atualizar'}
              </button>
            )}
          </div>
        </div>

        {/* Abas no padrao do resto do sistema: traco embaixo da selecionada. */}
        <div role="tablist" aria-label="Fonte dos registros" className="mb-3 flex gap-1 border-b border-line">
          {[
            { id: 'aplicacao' as const, rotulo: 'Aplicação' },
            { id: 'webhooks' as const, rotulo: 'Webhooks do Google' },
            { id: 'arquivos' as const, rotulo: 'Arquivos .txt' },
            /* HISTORICO GERAL veio da Home (29/09/2026, a pedido).

               Ele lista o que cada pessoa fez, com nome e IP - e leitura de
               AUDITORIA, nao de operacao. Na Home disputava espaco com os
               numeros do parque e era a primeira coisa que alguem via ao entrar,
               sem ter perguntado nada.

               Aqui fica ao lado das outras tres fontes de registro, que
               respondem a mesma pergunta ("o que aconteceu no sistema"), e a
               tela inteira ja e restrita a Master. */
            { id: 'historico' as const, rotulo: 'Histórico geral' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={aba === t.id}
              onClick={() => setAba(t.id)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-[12.5px] font-semibold',
                aba === t.id ? 'border-primary text-ink' : 'border-transparent text-muted-foreground hover:text-ink'
              )}
            >
              {t.rotulo}
            </button>
          ))}
        </div>

        {aba === 'aplicacao' && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            value={nivel}
            onChange={(e) => setNivel(e.target.value)}
            className="h-8 rounded-lg border border-line bg-surface px-2 text-[12px]"
          >
            {NIVEIS.map((n) => (
              <option key={n.valor} value={n.valor}>
                {n.rotulo}
              </option>
            ))}
          </select>

          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procurar na mensagem ou no contexto"
            className="h-8 w-full max-w-[320px] rounded-lg border border-line bg-surface px-3 text-[12px] outline-none focus:border-primary"
          />

          <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
            Atualizar sozinho (5s)
          </label>

          <span className="ml-auto text-[11px] text-faint">
            <Num className="font-semibold text-muted-foreground">{filtrados.length}</Num> na tela
            {contagem && (
              <>
                {' · '}
                <Num>{contagem.guardados}</Num> de <Num>{contagem.capacidade}</Num> guardadas
              </>
            )}
          </span>
        </div>
        )}

        {erro && (
          <div className="mb-3 rounded-lg border border-danger bg-danger-soft p-3 text-[12.5px] text-danger">{erro}</div>
        )}

        {aba === 'aplicacao' && (
        <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
          <div className="grid items-center gap-2 border-b border-line bg-primary px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-primary-foreground" style={{ gridTemplateColumns: '150px 90px 190px 1fr' }}>
            <div>Quando</div>
            <div>Nível</div>
            <div>Origem</div>
            <div>Mensagem</div>
          </div>

          {!carregando && filtrados.length === 0 && (
            <div className="p-8 text-center text-[12.5px] text-muted-foreground">
              {itens.length === 0
                ? 'Nenhum registro no momento. A API guarda a partir do último reinício.'
                : 'Nenhuma linha com esse filtro.'}
            </div>
          )}

          {filtrados.map((e) => (
            <div
              key={e.id}
              /* A linha inteira alterna a pilha porque o alvo útil - a pilha de
                 um erro - não tem lugar próprio para clicar; e só erro tem
                 pilha, então nas outras o clique não faz nada. */
              onClick={() => setExpandido(expandido === e.id ? null : e.id)}
              className={cn(
                'grid items-start gap-2 border-b px-3 py-1.5 text-[12px]',
                e.pilha ? 'cursor-pointer hover:bg-surface-2' : ''
              )}
              style={{ gridTemplateColumns: '150px 90px 190px 1fr', borderColor: 'var(--gps-soft)' }}
            >
              <Num className="text-muted-foreground" title={dia(e.data)}>
                {hora(e.data)}
              </Num>

              <span className={cn('inline-flex w-fit rounded px-1.5 py-0.5 text-[10.5px] font-bold uppercase', TOM[e.nivel] || TOM.log)}>
                {e.nivel}
              </span>

              <span className="block truncate font-semibold" title={e.contexto}>
                {e.contexto}
              </span>

              <div className="min-w-0">
                {/* `break-words` e não `truncate`: mensagem de erro cortada no
                    meio não serve para nada, e é justamente a linha comprida
                    que interessa. */}
                <span className="block break-words">{e.mensagem}</span>
                {e.pilha && expandido === e.id && (
                  <pre className="mt-1 overflow-x-auto rounded bg-surface-2 p-2 text-[11px] text-muted-foreground">
                    {e.pilha}
                  </pre>
                )}
                {e.pilha && expandido !== e.id && (
                  <span className="text-[11px] text-faint">clique para ver a pilha</span>
                )}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* ------------------------------------------------ WEBHOOKS DO GOOGLE */}
        {aba === 'webhooks' && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <select
                value={statusWebhook}
                onChange={(e) => {
                  setPaginaWebhook(0);
                  setStatusWebhook(e.target.value);
                }}
                className="h-8 rounded-lg border border-line bg-surface px-2 text-[12px]"
              >
                {['todos', 'RECEBIDO', 'PROCESSADO', 'PENDENTE', 'ERRO'].map((v) => (
                  <option key={v} value={v}>
                    {v === 'todos' ? 'Todos os status' : v}
                  </option>
                ))}
              </select>

              <CaixaBusca
                valor={buscaWebhook}
                aoBuscar={(v) => {
                  setPaginaWebhook(0);
                  setBuscaWebhook(v);
                }}
                placeholder="Device ID, IMEI, serial ou mensagem de erro"
                className="w-full max-w-[360px]"
              />

              <button
                type="button"
                onClick={carregarWebhooks}
                disabled={carregando}
                className="h-8 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
              >
                {carregando ? 'Lendo...' : 'Atualizar'}
              </button>

              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <input type="checkbox" checked={soSemSerial} onChange={(e) => setSoSemSerial(e.target.checked)} />
                Só os que chegaram sem serial
              </label>

              {/* BAIXA O QUE ESTA NA TELA, e o rotulo diz isso.

                  Nao busca o historico inteiro de proposito: a tabela pode ter
                  centenas de milhares de linhas, e um botao que parece
                  instantaneo nao pode disparar uma consulta sem teto. Quem quer
                  um recorte maior aumenta a pagina ou filtra - e ai o arquivo
                  sai do mesmo jeito.

                  O PAYLOAD NAO ENTRA na planilha: sao KB de JSON por linha, o
                  arquivo ficaria pesado e a celula, ilegivel. Cada payload se
                  baixa em separado, pelo botao dentro da linha aberta. */}
              <BotaoExportar
                className="h-8 text-[12px]"
                rotulo={`Baixar (${webhooksVisiveis.length})`}
                nomeArquivo={`webhooks-${new Date().toISOString().slice(0, 10)}`}
                linhas={() =>
                  webhooksVisiveis.map((w) => ({
                    Quando: new Date(w.createdAt).toLocaleString('pt-BR'),
                    'Device ID': w.deviceId || '',
                    IMEI: w.imei || '',
                    Serial: w.serialNumber || '',
                    Status: w.status,
                    Evento: w.statusWebhook || '',
                    Origem: w.origem || '',
                    Tentativas: w.tentativas ?? 0,
                    Erro: w.errorMessage || '',
                    'Payload (KB)': (w.payloadBytes / 1024).toFixed(1),
                  }))
                }
              />

              <span className="ml-auto text-[11px] text-faint">
                <Num className="font-semibold text-muted-foreground">{totalWebhooks.toLocaleString('pt-BR')}</Num>{' '}
                recebido(s)
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
              <div
                className="grid items-center gap-2 border-b border-line bg-primary px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-primary-foreground"
                style={{ gridTemplateColumns: '132px 190px 140px 130px 110px 130px 1fr' }}
              >
                <div>Quando</div>
                <div>Device ID</div>
                {/* IMEI e SERIAL viraram colunas proprias: e olhando as duas
                    lado a lado que se percebe o caso relatado - aparelho que
                    chegou so com IMEI, quando o normal e o Google mandar o
                    serial. Escondidos no payload, isso nao se nota. */}
                <div>IMEI</div>
                <div>Serial</div>
                <div>Status</div>
                <div>Evento</div>
                <div>Erro</div>
              </div>

              {!carregando && webhooksVisiveis.length === 0 && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhum webhook com esse filtro.
                </div>
              )}

              {webhooksVisiveis.map((w) => (
                <div key={w.codigo}>
                  <div
                    onClick={() => abrirPayload(w.codigo)}
                    className="grid cursor-pointer items-start gap-2 border-b px-3 py-1.5 text-[12px] hover:bg-surface-2"
                    style={{ gridTemplateColumns: '132px 190px 140px 130px 110px 130px 1fr', borderColor: 'var(--gps-soft)' }}
                  >
                    <Num className="text-muted-foreground" title={new Date(w.createdAt).toLocaleString('pt-BR')}>
                      {dia(w.createdAt)} {hora(w.createdAt).slice(0, 8)}
                    </Num>

                    <Num className="block truncate" title={w.deviceId || ''}>
                      {w.deviceId || '-'}
                    </Num>

                    <Num className={cn('block truncate', w.imei ? '' : 'text-faint')} title={w.imei || ''}>
                      {w.imei || '-'}
                    </Num>

                    {/* Serial ausente ganha destaque em ambar: nao e erro, mas e
                        a anomalia que se esta procurando. */}
                    <Num
                      className={cn('block truncate', w.serialNumber ? '' : 'font-bold text-warn')}
                      title={w.serialNumber || 'Chegou sem serial'}
                    >
                      {w.serialNumber || 'sem serial'}
                    </Num>

                    <span
                      className={cn(
                        'inline-flex w-fit rounded px-1.5 py-0.5 text-[10.5px] font-bold',
                        w.status === 'ERRO'
                          ? 'bg-danger-soft text-danger'
                          : w.status === 'PENDENTE'
                            ? 'bg-warn-soft text-warn'
                            : 'bg-ok-soft text-ok'
                      )}
                    >
                      {w.status}
                    </span>

                    <span className="block truncate text-muted-foreground" title={w.statusWebhook || ''}>
                      {w.statusWebhook || '-'}
                    </span>

                    {/* O erro fica na linha, sem precisar abrir: e a coluna que
                        se varre com o olho quando algo deu errado. */}
                    <span className={cn('block break-words', w.errorMessage ? 'text-danger' : 'text-faint')}>
                      {w.errorMessage || (w.tentativas > 1 ? `${w.tentativas} tentativas` : '-')}
                    </span>

                  </div>

                  {payload?.codigo === w.codigo && (
                    <div className="border-b bg-surface-2" style={{ borderColor: 'var(--gps-soft)' }}>
                      <div className="flex items-center justify-between gap-2 px-3 pt-2">
                        <span className="text-[11px] font-semibold text-muted-foreground">
                          Payload recebido do Google
                        </span>
                        <button
                          type="button"
                          /* `stopPropagation`: a linha inteira alterna o
                             payload, e sem isto baixar fecharia o bloco. */
                          onClick={(e) => {
                            e.stopPropagation();
                            baixarPayload(w.codigo, payload.texto);
                          }}
                          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line bg-surface px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                        >
                          <svg width="12" height="12" viewBox="0 0 18 18" fill="none" aria-hidden>
                            <path
                              d="M9 2.5v9 M5.5 8.5L9 12l3.5-3.5 M3.5 14.5h11"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Baixar .json
                        </button>
                      </div>
                      <pre className="max-h-[420px] overflow-auto p-3 text-[11px] text-muted-foreground">
                        {payload.texto}
                      </pre>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2 text-[12px] text-muted-foreground">
                <span>
                  Mostrando <Num>{webhooksVisiveis.length}</Num> de <Num>{totalWebhooks.toLocaleString('pt-BR')}</Num>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={paginaWebhook === 0}
                    onClick={() => setPaginaWebhook((p) => Math.max(0, p - 1))}
                    className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <span>
                    <Num>{paginaWebhook + 1}</Num> / <Num>{Math.max(1, Math.ceil(totalWebhooks / 25))}</Num>
                  </span>
                  <button
                    type="button"
                    disabled={(paginaWebhook + 1) * 25 >= totalWebhooks}
                    onClick={() => setPaginaWebhook((p) => p + 1)}
                    className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                  >
                    Proxima
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-2 text-[11px] text-faint">
              Vem da tabela <span className="tabular-nums">TAB_WEBHOOK_LOG</span>, onde todo webhook recebido do
              Google e gravado com o payload inteiro - tem historico e sobrevive a reinicio, ao contrario da aba
              Aplicacao. O arquivo <span className="tabular-nums">logs/eventos.txt</span> no servidor e sobra de
              depuracao e nao precisa mais ser aberto.
            </p>
          </>
        )}

        {/* ----------------------------------------------- ARQUIVOS .txt */}
        {aba === 'arquivos' && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={carregarArquivos}
                disabled={carregando}
                className="h-8 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
              >
                {carregando ? 'Lendo...' : 'Atualizar'}
              </button>
              <span className="text-[11px] text-faint">
                <Num className="font-semibold text-muted-foreground">{arquivos.length}</Num> arquivo(s)
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
              <div
                className="grid items-center gap-2 border-b border-line bg-primary px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-primary-foreground"
                style={{ gridTemplateColumns: 'minmax(200px,1.4fr) minmax(200px,1fr) 110px 150px' }}
              >
                <div>Arquivo</div>
                <div>Pasta</div>
                <div className="text-right">Tamanho</div>
                <div>Modificado</div>
              </div>

              {!carregando && arquivos.length === 0 && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhum arquivo .txt nas pastas de log.
                </div>
              )}

              {arquivos.map((a) => (
                <div key={a.pasta + a.nome}>
                  <div
                    onClick={() => abrirArquivo(a.nome)}
                    className="grid cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-[12px] hover:bg-surface-2"
                    style={{ gridTemplateColumns: 'minmax(200px,1.4fr) minmax(200px,1fr) 110px 150px', borderColor: 'var(--gps-soft)' }}
                  >
                    <span className="block truncate font-semibold">{a.nome}</span>
                    <span className="block truncate text-muted-foreground">{a.pasta}</span>
                    <Num className="text-right text-muted-foreground">{(a.bytes / 1024).toFixed(1)} KB</Num>
                    <Num className="text-muted-foreground">
                      {dia(a.modificado)} {hora(a.modificado).slice(0, 5)}
                    </Num>
                  </div>

                  {arquivoAberto?.nome === a.nome && (
                    <div className="border-b bg-surface-2" style={{ borderColor: 'var(--gps-soft)' }}>
                      <div className="flex items-center justify-between gap-2 px-3 pt-2">
                        <span className="text-[11px] font-semibold text-muted-foreground">{arquivoAberto.pasta}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            /* Baixa O QUE ESTA NA TELA. Em arquivo grande isso
                               e o fim dele, nao o arquivo inteiro - e o texto no
                               topo do conteudo avisa quando foi cortado. */
                            const blob = new Blob([arquivoAberto.conteudo], { type: 'text/plain;charset=utf-8' });
                            const url = URL.createObjectURL(blob);
                            const el = document.createElement('a');
                            el.href = url;
                            el.download = a.nome;
                            document.body.appendChild(el);
                            el.click();
                            document.body.removeChild(el);
                            URL.revokeObjectURL(url);
                          }}
                          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line bg-surface px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                        >
                          <svg width="12" height="12" viewBox="0 0 18 18" fill="none" aria-hidden>
                            <path
                              d="M9 2.5v9 M5.5 8.5L9 12l3.5-3.5 M3.5 14.5h11"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Baixar
                        </button>
                      </div>
                      <pre className="max-h-[460px] overflow-auto p-3 text-[11px] text-muted-foreground">
                        {arquivoAberto.conteudo || '(arquivo vazio)'}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ONDE A API PROCUROU. Sem isso, "nenhum arquivo" e ambiguo: nao
                se sabe se nao ha arquivo ou se a pasta e outra - que foi
                exatamente o que aconteceu com o eventos.txt. */}
            {pastasLidas.length > 0 && (
              <p className="mt-2 text-[11px] text-faint">
                Pastas consultadas: {pastasLidas.join(' · ')}
              </p>
            )}
            <p className="mt-1 text-[11px] text-faint">
              A API grava em <span className="tabular-nums">dist/logs/</span> (o caminho é relativo ao código
              compilado), e não no <span className="tabular-nums">logs/</span> da raiz do projeto — por isso o
              arquivo parecia não existir. Arquivo grande é mostrado só pelo fim.
            </p>
          </>
        )}

        {/* ------------------------------------------- HISTORICO GERAL */}
        {aba === 'historico' && (
          <>
            <p className="mb-3 text-[12px] text-muted-foreground">
              O que cada pessoa fez no sistema na última hora — criações, alterações, remoções e
              acessos, com o usuário e o endereço de onde partiu.
            </p>
            <HistoricoGeral />
          </>
        )}

        {aba === 'aplicacao' && (
        <p className="mt-2 text-[11px] text-faint">
          Os registros ficam na memória do processo da API: reiniciar o servidor limpa a lista, e o que
          aparece aqui é o do processo que respondeu a esta consulta. Para investigar algo antigo, use o
          log do servidor (<span className="tabular-nums">pm2 logs</span>).
        </p>
        )}
      </div>
    </Page>
  );
}
